import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paper, Stack, Typography } from "@mui/material";
import EnterpriseForm from "./EnterpriseConfig/EnterpriseForm";
import EnterpriseModals from "./EnterpriseConfig/EnterpriseModals";
import { useAuth } from "../../context/AuthContext";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAlert } from "../../context/AlertContext";

const API_BASE = "/api/enterprise";

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return "";
};

const buildHeaders = (token, isFormData = false) => {
  const headers = {
    Authorization: `Bearer ${token}`,
    "X-CSRFToken": getCookie("csrftoken"),
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
};

const normalizeCode = (value) => {
  const only = String(value || "").replace(/\D/g, "").slice(0, 3);
  return only ? only.padStart(3, "0") : "";
};

const isRimpeValue = (value) => {
  return value === "EMPRENDEDOR" || value === "NEGOCIO_POPULAR";
};

const normalizeRegimenTipo = (value) => {
  if (value === "RIMPE_EMPRENDEDOR") return "EMPRENDEDOR";
  if (value === "RIMPE_NEGOCIO_POPULAR") return "NEGOCIO_POPULAR";
  return value || "GENERAL";
};

const normalizeEstablecimientos = (empresa) => {
  if (Array.isArray(empresa?.establecimientos) && empresa.establecimientos.length > 0) {
    return empresa.establecimientos
      .map((e) => {
        const codigo = normalizeCode(e.codigo);

        return {
          id: e.id || null,
          codigo,
          nombre: String(e.nombre || "").trim() || `ESTABLECIMIENTO ${codigo}`,
          direccion: String(e.direccion || "").trim(),
          activo: typeof e.activo === "boolean" ? e.activo : true,
        };
      })
      .filter((e) => e.codigo);
  }

  const raw = String(empresa?.numero_establecimiento || "").trim();

  if (raw) {
    const seen = new Set();

    return raw
      .split(/[,\s;|]+/)
      .map((item) => normalizeCode(item))
      .filter(Boolean)
      .filter((codigo) => {
        if (seen.has(codigo)) return false;
        seen.add(codigo);
        return true;
      })
      .map((codigo) => ({
        id: null,
        codigo,
        nombre: `ESTABLECIMIENTO ${codigo}`,
        direccion: empresa?.direccion || "",
        activo: true,
      }));
  }

  return [];
};

const getNumeroEstablecimientoFromList = (establecimientos) => {
  if (!Array.isArray(establecimientos)) return "";

  return establecimientos
    .map((item) => normalizeCode(item.codigo))
    .filter(Boolean)
    .join(", ");
};

const getErrorMessage = (error) => {
  if (!error) return "Ocurrió un error inesperado";
  if (typeof error === "string") return error;

  if (Array.isArray(error)) {
    return error.join(", ");
  }

  if (typeof error === "object") {
    const firstKey = Object.keys(error)[0];

    if (firstKey) {
      const value = error[firstKey];

      if (Array.isArray(value)) {
        return `${firstKey}: ${value.join(", ")}`;
      }

      if (typeof value === "string") {
        return `${firstKey}: ${value}`;
      }
    }

    return JSON.stringify(error);
  }

  return "Ocurrió un error inesperado";
};

const defaultEmpresa = {
  id: null,
  ruc: "",
  razon_social: "",
  nombre_comercial: "",
  numero_establecimiento: "",
  establecimientos: [],
  obligado_contabilidad: "NO",
  contribuyente_especial: false,
  gran_contribuyente: false,
  exportador: false,
  exportador_tipo: "NO_HABITUAL",
  contribuyente_rimpe: false,
  rimpe_tipo: "",
  regimen_tipo: "GENERAL",
  regimen_general: true,
  agente_retencion: false,
  ciudad: "",
  telefonos: "",
  direccion: "",
  email_notificacion: "",
  logo_empresa: null,
};

const EnterpriseConfig = () => {
  const { user, accessToken, logout } = useAuth();
  const { dashboardTheme } = useDashboardTheme();
  const alert = useAlert();

  const [empresa, setEmpresa] = useState(defaultEmpresa);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const logoRef = useRef(null);

  const permissions = user?.permissions || [];
  const isSuper = user?.role === "super_admin";

  const canView = isSuper || permissions.includes("enterprise.view");
  const canCreate = isSuper || permissions.includes("enterprise.create");
  const canEdit = isSuper || permissions.includes("enterprise.edit");
  const canDelete = isSuper || permissions.includes("enterprise.delete");

  const canSave = empresa.id ? canEdit : canCreate;

  const notify = useCallback(
    (message, severity = "success") => {
      if (typeof alert?.showAlert === "function") {
        try {
          alert.showAlert(message, severity);
          return;
        } catch {
          alert.showAlert({ message, severity });
          return;
        }
      }

      if (typeof alert?.showSnackbar === "function") {
        try {
          alert.showSnackbar(message, severity);
          return;
        } catch {
          alert.showSnackbar({ message, severity });
          return;
        }
      }

      if (typeof alert?.setAlert === "function") {
        alert.setAlert({ open: true, message, severity });
      }
    },
    [alert]
  );

  const getToken = useCallback(() => {
    return accessToken || localStorage.getItem("accessToken") || "";
  }, [accessToken]);

  const hydrateEmpresa = useCallback((e) => {
    const establecimientos = normalizeEstablecimientos(e);
    const regimenTipo = normalizeRegimenTipo(e.regimen_tipo);

    setEmpresa((prev) => ({
      ...prev,
      ...e,
      id: e.id ?? prev.id,
      establecimientos,
      numero_establecimiento: getNumeroEstablecimientoFromList(establecimientos),
      exportador: !!e.exportador,
      contribuyente_especial: !!e.contribuyente_especial,
      gran_contribuyente: !!e.gran_contribuyente,
      agente_retencion: !!e.agente_retencion,
      contribuyente_rimpe: isRimpeValue(regimenTipo) ? true : !!e.contribuyente_rimpe,
      regimen_general: regimenTipo === "GENERAL",
      rimpe_tipo: isRimpeValue(regimenTipo) ? regimenTipo : "",
      regimen_tipo: regimenTipo,
      logo_empresa: e.logo_empresa ?? null,
    }));
  }, []);

  const loadEmpresa = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }

    const token = getToken();

    if (!token) {
      notify("Sesión vencida. Inicia sesión nuevamente.", "error");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/empresa/obtener/`, {
        method: "GET",
        credentials: "include",
        headers: buildHeaders(token, false),
      });

      let data = {};

      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        setLoading(false);
        return;
      }

      if (res.ok && data?.empresa) {
        hydrateEmpresa(data.empresa);
        return;
      }

      if (!res.ok) {
        notify(getErrorMessage(data?.error), "error");
      }
    } catch {
      notify("No se pudo cargar la configuración de la empresa.", "error");
    } finally {
      setLoading(false);
    }
  }, [canView, getToken, hydrateEmpresa, notify, logout]);

  useEffect(() => {
    loadEmpresa();
  }, [loadEmpresa]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setEmpresa((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "regimen_tipo") {
        const regimenTipo = normalizeRegimenTipo(value);
        next.regimen_tipo = regimenTipo;
        next.contribuyente_rimpe = isRimpeValue(regimenTipo);
        next.regimen_general = regimenTipo === "GENERAL";
        next.rimpe_tipo = isRimpeValue(regimenTipo) ? regimenTipo : "";
      }

      if (name === "rimpe_tipo") {
        const rimpeTipo = normalizeRegimenTipo(value);
        next.rimpe_tipo = rimpeTipo;
        next.regimen_tipo = rimpeTipo;
        next.contribuyente_rimpe = isRimpeValue(rimpeTipo);
        next.regimen_general = false;
      }

      return next;
    });
  };

  const handleCheck = (e) => {
    const { name, checked } = e.target;

    setEmpresa((prev) => {
      const next = { ...prev, [name]: checked };

      if (name === "exportador" && !checked) {
        next.exportador_tipo = "NO_HABITUAL";
      }

      if (name === "contribuyente_rimpe") {
        if (checked) {
          next.regimen_general = false;
          next.regimen_tipo = next.rimpe_tipo || "EMPRENDEDOR";
          next.rimpe_tipo = next.regimen_tipo;
        } else {
          next.rimpe_tipo = "";
          next.regimen_tipo = "GENERAL";
          next.regimen_general = true;
        }
      }

      if (name === "regimen_general") {
        if (checked) {
          next.contribuyente_rimpe = false;
          next.rimpe_tipo = "";
          next.regimen_tipo = "GENERAL";
        } else if (!next.contribuyente_rimpe) {
          next.regimen_tipo = "";
        }
      }

      return next;
    });
  };

  const handleFile = (e) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const file = files[0];
      const ext = file.name.split(".").pop().toLowerCase();
      const allowed = ["jpg", "jpeg", "png"];

      if (!allowed.includes(ext)) {
        notify("Formato no permitido. Solo JPG, JPEG y PNG.", "error");
        e.target.value = "";
        return;
      }

      setEmpresa((prev) => ({ ...prev, [name]: file }));
    }
  };

  const openPreview = (fileOrUrl) => {
    if (!fileOrUrl) return;

    const url = typeof fileOrUrl === "string" ? fileOrUrl : URL.createObjectURL(fileOrUrl);
    setPreview(url);
    setOpenModal(true);
  };

  const closePreview = () => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setOpenModal(false);
    setPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!canSave) {
      notify("No tienes permisos para guardar esta configuración.", "error");
      return;
    }

    const token = getToken();

    if (!token) {
      notify("Sesión vencida. Inicia sesión nuevamente.", "error");
      return;
    }

    const establecimientos = normalizeEstablecimientos(empresa);

    if (!establecimientos.length) {
      notify("Ingrese al menos un establecimiento válido.", "error");
      return;
    }

    if (empresa.contribuyente_rimpe && !empresa.rimpe_tipo && !empresa.regimen_tipo) {
      notify("Seleccione el tipo de RIMPE.", "error");
      return;
    }

    const isEdit = !!empresa.id;
    const url = isEdit
      ? `${API_BASE}/empresa/editar/${empresa.id}/`
      : `${API_BASE}/empresa/crear/`;

    const regimen_tipo = empresa.regimen_general
      ? "GENERAL"
      : empresa.contribuyente_rimpe
      ? normalizeRegimenTipo(empresa.rimpe_tipo || empresa.regimen_tipo || "EMPRENDEDOR")
      : "GENERAL";

    const form = new FormData();

    form.append("ruc", empresa.ruc || "");
    form.append("razon_social", empresa.razon_social || "");
    form.append("nombre_comercial", empresa.nombre_comercial || "");
    form.append("numero_establecimiento", getNumeroEstablecimientoFromList(establecimientos));
    form.append("establecimientos", JSON.stringify(establecimientos));
    form.append("obligado_contabilidad", empresa.obligado_contabilidad || "NO");
    form.append("contribuyente_especial", String(!!empresa.contribuyente_especial));
    form.append("gran_contribuyente", String(!!empresa.gran_contribuyente));
    form.append("exportador", String(!!empresa.exportador));
    form.append("exportador_tipo", empresa.exportador ? empresa.exportador_tipo || "NO_HABITUAL" : "");
    form.append("agente_retencion", String(!!empresa.agente_retencion));
    form.append("ciudad", empresa.ciudad || "");
    form.append("telefonos", empresa.telefonos || "");
    form.append("direccion", empresa.direccion || "");
    form.append("email_notificacion", empresa.email_notificacion || "");
    form.append("regimen_tipo", regimen_tipo);
    form.append("contribuyente_rimpe", String(!!empresa.contribuyente_rimpe));

    if (empresa.logo_empresa && typeof empresa.logo_empresa !== "string") {
      form.append("logo_empresa", empresa.logo_empresa);
    }

    try {
      setSaving(true);

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        credentials: "include",
        headers: buildHeaders(token, true),
        body: form,
      });

      let data = {};

      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok) {
        notify(data?.mensaje || "Datos guardados correctamente", "success");
        await loadEmpresa();

        if (logoRef.current) {
          logoRef.current.value = "";
        }

        return;
      }

      if (res.status === 403) {
        notify(data?.error || "Permisos insuficientes.", "error");
        return;
      }

      if (res.status === 405) {
        notify("Método no permitido. Revisa la URL del endpoint.", "error");
        return;
      }

      notify(getErrorMessage(data?.error), "error");
    } catch {
      notify("No se pudo procesar la solicitud.", "error");
    } finally {
      setSaving(false);
    }
  };

  const fileLabel = (file) => {
    if (!file) return "Imagen no seleccionada";
    if (typeof file === "string") return file.split("/").pop();
    return file.name || "Imagen seleccionada";
  };

  if (!canView) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: "14px",
          border: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
        }}
      >
        <Stack spacing={0.7}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: dashboardTheme.text_color,
            }}
          >
            Sin permisos
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: dashboardTheme.muted_text_color,
              lineHeight: 1.6,
            }}
          >
            No tienes permisos para ver la configuración de la empresa.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        overflow: "hidden",
        border: `1px solid ${dashboardTheme.border_color}`,
        bgcolor: dashboardTheme.card_background,
        boxShadow:
          dashboardTheme.theme_mode === "dark"
            ? "none"
            : "0 4px 14px rgba(0,0,0,0.08)",
      }}
    >
      <EnterpriseForm
        empresa={empresa}
        setEmpresa={setEmpresa}
        handleChange={handleChange}
        handleCheck={handleCheck}
        handleFile={handleFile}
        fileLabel={fileLabel}
        openPreview={openPreview}
        submit={submit}
        logoRef={logoRef}
        loading={loading}
        saving={saving}
        canSave={canSave}
        canCreate={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      <EnterpriseModals
        openModal={openModal}
        preview={preview}
        closePreview={closePreview}
      />
    </Paper>
  );
};

export default EnterpriseConfig;